import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { Cluster, MarkerClusterer, Renderer } from '@googlemaps/markerclusterer';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { GoogleMapsLoaderService } from './google-maps-loader.service';
import {
  JOB_TYPE_OPTIONS,
  JobMapCity,
  JobMapJobType,
  JobMapPin,
  JobMapService,
} from './job-map.service';

interface JobMapPinWithDistance extends JobMapPin {
  distanceKm: number | null;
}

/**
 * Strip diacritics (Lithuanian/Latvian/Estonian) and lowercase, so a user
 * typing "riga" matches "Rīga" / "Rīgas" in the dropdown, and an address
 * containing "Klaipėda" still matches a city stored as "klaip".
 */
function normalizeForMatch(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  // ̀-ͯ is the Unicode "Combining Diacritical Marks" block.
  // After NFD decomposition, e.g. "Rīga" becomes "R" + "i" + COMBINING MACRON
  // + "g" + "a". Stripping that block yields plain ASCII "Riga".
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Vilnius city center — sensible LT-first default. */
const DEFAULT_CENTER: google.maps.LatLngLiteral = { lat: 54.6872, lng: 25.2797 };
const DEFAULT_ZOOM = 7;
const FOCUSED_ZOOM = 11;
const DEFAULT_RADIUS_KM = 25;

/** Debounce window for re-geocoding while the user types the address. */
const ADDRESS_DEBOUNCE_MS = 500;

/**
 * Status-driven marker colors. Sourced from `src/colors.scss` so the map
 * matches the convention used in the rest of Boris (status pills, planner,
 * order list, etc):
 *
 *   $progress-success-color    -> green for CONFIRMED ("work locked in")
 *   $progress-inprogress-color -> yellow for IN_PROGRESS ("being filled")
 *   $blue-nice                 -> blue as a neutral fallback
 *
 * Used for both individual pin icons and the cluster bubble (where the
 * majority status of the contained markers wins).
 */
const PIN_COLOR_BY_STATUS: Record<string, string> = {
  CONFIRMED: '#88B153',   // colors.scss $progress-success-color
  IN_PROGRESS: '#FFC107', // colors.scss $progress-inprogress-color
};
const PIN_COLOR_FALLBACK = '#039be5'; // colors.scss $blue-nice

/** Custom data key we set on each google.maps.Marker so the cluster renderer can read it back. */
const MARKER_STATUS_KEY = '_pinStatus';

/**
 * Light-touch basemap tuning. Lets Google's natural colours come through —
 * we only adjust *what is visible*, not the underlying palette. The map
 * should feel like normal Google Maps, just without restaurant/shop clutter.
 *
 * What's visible:
 *   - All streets and their names (every zoom level)
 *   - Hospitals, schools, government, parks, attractions, places of worship
 *   - Transit stations (but not the coloured transit-line overlays)
 *   - Default land/water colours from Google
 *
 * What's hidden:
 *   - Business POIs (restaurants, shops, cafes, hotels)
 *   - Transit lines (just the route overlays)
 *   - Tiny administrative labels (neighbourhoods, land parcels)
 *
 * Applied via `<google-map [options]>`, no Google Cloud config required.
 */
const MINIMAL_MAP_STYLES: google.maps.MapTypeStyle[] = [
  // Drop only the tiniest admin labels — keep cities, countries, boundaries.
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },

  // POIs — broad sweep off, then re-enable the categories worth keeping.
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.medical', stylers: [{ visibility: 'on' }] },
  { featureType: 'poi.school', stylers: [{ visibility: 'on' }] },
  { featureType: 'poi.government', stylers: [{ visibility: 'on' }] },
  { featureType: 'poi.park', stylers: [{ visibility: 'on' }] },
  { featureType: 'poi.attraction', stylers: [{ visibility: 'on' }] },
  { featureType: 'poi.place_of_worship', stylers: [{ visibility: 'on' }] },
  // `poi.business` (restaurants/shops/cafes/hotels) intentionally left off.

  // Transit — kill the line overlays, keep stations as landmarks.
  { featureType: 'transit.line', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit.station', stylers: [{ visibility: 'on' }] },
];

/**
 * Standard Material "place" pin shape — a teardrop with a circular hole.
 * Hand-rolled SVG path so we don't need to ship an image asset.
 */
const PIN_SVG_PATH =
  'M12,2C8.13,2 5,5.13 5,9c0,5.25 7,13 7,13s7,-7.75 7,-13c0,-3.87 -3.13,-7 -7,-7zM12,11.5c-1.38,0 -2.5,-1.12 -2.5,-2.5s1.12,-2.5 2.5,-2.5 2.5,1.12 2.5,2.5 -1.12,2.5 -2.5,2.5z';

@Component({
  selector: 'app-job-map',
  templateUrl: './job-map.component.html',
  styleUrls: ['./job-map.component.scss'],
})
export class JobMapComponent implements OnInit, OnDestroy {
  @ViewChild(GoogleMap) googleMap?: GoogleMap;

  mapsReady = false;
  loading = true;

  /** All active jobs returned by the backend. */
  allPins: JobMapPin[] = [];

  /** Pins that pass the current filter set, with distance computed. */
  visiblePins: JobMapPinWithDistance[] = [];

  center: google.maps.LatLngLiteral = DEFAULT_CENTER;
  zoom = DEFAULT_ZOOM;

  /** Search radius in meters (for <map-circle>). */
  get radiusMeters(): number {
    return this.radiusKm * 1000;
  }

  /** User input: free-text address. Empty by default — Lithuania-wide view until the recruiter types something. */
  addressControl = new FormControl('');

  /** Selected radius in kilometers. */
  radiusKm = DEFAULT_RADIUS_KM;

  /** Bounds for the radius slider. Keep narrow at the low end so 2-3km is reachable. */
  readonly radiusMin = 1;
  readonly radiusMax = 100;

  /** Selected city IDs (empty array = no filter). Relay IDs are strings. */
  cityControl = new FormControl<string[]>([]);

  /** Selected order_type codes (empty array = no filter). */
  jobTypeControl = new FormControl<string[]>([]);

  /** Free-text search inside the city dropdown — fed into ngx-mat-select-search. */
  citySearchControl = new FormControl<string>('');

  hasResolvedLocation = false;
  resolveError: string | null = null;

  /** Populated from the existing `regions` query (locations.schema.RegionType). */
  cityOptions: JobMapCity[] = [];

  /** `cityOptions` filtered by `citySearchControl` — bound to the dropdown's options. */
  filteredCityOptions: JobMapCity[] = [];

  /** Static frontend constant — see JOB_TYPE_OPTIONS in job-map.service.ts. */
  readonly jobTypeOptions: JobMapJobType[] = JOB_TYPE_OPTIONS;

  /** Fast lookup of order_type code → label for the InfoWindow and side panel. */
  private readonly jobTypeLabelByValue = new Map<string, string>(
    JOB_TYPE_OPTIONS.map((t) => [t.value, t.label]),
  );

  /** Fast lookup of city id → row, used by the filter. */
  private cityById = new Map<string, JobMapCity>();

  /** Currently-open info-window pin, or null. Drives the popup template. */
  openInfoPin: JobMapPinWithDistance | null = null;

  /**
   * Options bound to <google-map>. Combines the minimalist base style with
   * UI tweaks: drop the Map/Satellite switcher, drop Street View, disable
   * accidental POI clicks. Zoom + fullscreen kept — they're useful.
   */
  readonly mapOptions: google.maps.MapOptions = {
    styles: MINIMAL_MAP_STYLES,
    disableDefaultUI: false,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
    zoomControl: true,
    clickableIcons: false,
    gestureHandling: 'greedy',
  };

  /** Circle styling for the search-radius overlay. */
  readonly radiusCircleOptions: google.maps.CircleOptions = {
    strokeColor: '#1976d2',
    strokeOpacity: 0.5,
    strokeWeight: 1,
    fillColor: '#1976d2',
    fillOpacity: 0.08,
    clickable: false,
  };

  /**
   * Cluster bubble renderer — minimalist flat disc, no shadow, no gradient.
   *
   * Colour: the majority status of the contained markers (green/yellow/blue
   * per the system palette). Size grows gently with member count.
   */
  readonly clusterRenderer: Renderer = {
    render: (cluster: Cluster) => {
      const count = cluster.count;
      const markers = cluster.markers || [];

      // Tally statuses across the cluster to pick the bubble colour.
      const counts: Record<string, number> = {};
      for (const m of markers) {
        const status = ((m as google.maps.Marker).get(MARKER_STATUS_KEY) as string) || '';
        counts[status] = (counts[status] || 0) + 1;
      }
      const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
      const majority = sorted.length ? sorted[0][0] : '';
      const color = PIN_COLOR_BY_STATUS[majority] || PIN_COLOR_FALLBACK;

      // Size scales gently with count: small / medium / large.
      // SymbolPath.CIRCLE uses `scale` as the radius in pixels.
      const radius = count < 10 ? 18 : count < 100 ? 22 : 26;

      return new google.maps.Marker({
        position: cluster.position,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: radius,
          fillColor: color,
          fillOpacity: 0.92,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
        label: {
          text: String(count),
          color: '#ffffff',
          fontSize: '13px',
          fontWeight: '600',
        },
        // Clusters render above individual pins.
        zIndex: 1000 + count,
      });
    },
  };

  private destroy$ = new Subject<void>();
  private markerCluster?: MarkerClusterer;
  /** Markers indexed by pin.id so a side-panel click can locate one. */
  private markerByPinId = new Map<string, google.maps.Marker>();
  private candidateMarker?: google.maps.Marker;
  private infoWindow?: google.maps.InfoWindow;
  private mapClickListenerAttached = false;

  constructor(
    private jobMapService: JobMapService,
    private mapsLoader: GoogleMapsLoaderService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.mapsLoader
      .load()
      .pipe(takeUntil(this.destroy$))
      .subscribe((ready) => {
        this.mapsReady = ready;
        if (ready) {
          this.fetchJobs();
          this.fetchCities();
          // Job types come from the static JOB_TYPE_OPTIONS constant — no fetch needed.
        } else {
          this.loading = false;
        }
      });

    this.citySearchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((q) => {
      this.applyCitySearch(q || '');
    });

    // Auto-apply when the user stops typing the address — no Apply press.
    this.addressControl.valueChanges
      .pipe(debounceTime(ADDRESS_DEBOUNCE_MS), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.mapsReady) {
          this.applyFilters();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearMarkers();
    if (this.infoWindow) {
      this.infoWindow.close();
      this.infoWindow = undefined;
    }
  }

  /** Apply button: geocode the address, then recompute filters. */
  applyFilters(): void {
    const address = (this.addressControl.value || '').trim();
    if (!address) {
      this.center = DEFAULT_CENTER;
      this.zoom = DEFAULT_ZOOM;
      this.hasResolvedLocation = false;
      this.resolveError = null;
      this.recomputeVisiblePins();
      return;
    }

    this.resolveError = null;
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address, region: 'lt' }, (results, status) => {
      if (status !== 'OK' || !results || !results.length) {
        this.resolveError = 'NO_RESULT';
        this.hasResolvedLocation = false;
        return;
      }
      const loc = results[0].geometry.location;
      this.center = { lat: loc.lat(), lng: loc.lng() };
      this.zoom = FOCUSED_ZOOM;
      this.hasResolvedLocation = true;
      this.recomputeVisiblePins();
    });
  }

  resetFilters(): void {
    this.addressControl.setValue('');
    this.radiusKm = DEFAULT_RADIUS_KM;
    this.cityControl.setValue([]);
    this.jobTypeControl.setValue([]);
    this.citySearchControl.setValue('');
    this.center = DEFAULT_CENTER;
    this.zoom = DEFAULT_ZOOM;
    this.hasResolvedLocation = false;
    this.resolveError = null;
    this.closeInfoWindow();
    this.recomputeVisiblePins();
  }

  /** Side-panel card click → open the same info window the marker would. */
  focusPin(pin: JobMapPinWithDistance): void {
    this.center = { lat: pin.latitude, lng: pin.longitude };
    this.zoom = 14;
    this.openInfoWindowFor(pin);
  }

  trackPinById(index: number, pin: JobMapPinWithDistance): string {
    return pin.id;
  }

  trackCityById(index: number, city: JobMapCity): string {
    return city.id;
  }

  trackOrderTypeByValue(index: number, opt: JobMapJobType): string {
    return opt.value;
  }

  /** Filter dropdowns call this on selection change. */
  onFilterChange(): void {
    this.recomputeVisiblePins();
  }

  /**
   * Toggle every currently-visible city on or off. "Visible" respects the
   * dropdown search so the user can search "vilni", click Select All, and
   * only Vilniaus-related options get selected.
   */
  toggleAllCities(): void {
    const visibleIds = this.filteredCityOptions.map((c) => c.id);
    const current = this.cityControl.value || [];
    const allVisibleSelected =
      visibleIds.length > 0 && visibleIds.every((id) => current.includes(id));

    if (allVisibleSelected) {
      this.cityControl.setValue(current.filter((id) => !visibleIds.includes(id)));
    } else {
      const merged = new Set<string>([...current, ...visibleIds]);
      this.cityControl.setValue(Array.from(merged));
    }
    this.recomputeVisiblePins();
  }

  /** True when every visible city is in the current selection. */
  areAllCitiesSelected(): boolean {
    const visibleIds = this.filteredCityOptions.map((c) => c.id);
    if (!visibleIds.length) {
      return false;
    }
    const current = this.cityControl.value || [];
    return visibleIds.every((id) => current.includes(id));
  }

  /** Toggle every job type at once. */
  toggleAllJobTypes(): void {
    const allValues = this.jobTypeOptions.map((t) => t.value);
    const current = this.jobTypeControl.value || [];
    const allSelected = allValues.length > 0 && allValues.every((v) => current.includes(v));
    this.jobTypeControl.setValue(allSelected ? [] : allValues);
    this.recomputeVisiblePins();
  }

  areAllJobTypesSelected(): boolean {
    if (!this.jobTypeOptions.length) {
      return false;
    }
    const current = this.jobTypeControl.value || [];
    return this.jobTypeOptions.every((t) => current.includes(t.value));
  }

  /**
   * Slider drag → just recompute pins (and the visible radius circle).
   * No need to re-geocode the address — that's only the Apply button's job.
   */
  onRadiusChange(value: number | null): void {
    if (typeof value === 'number' && value > 0) {
      this.radiusKm = value;
      this.recomputeVisiblePins();
    }
  }

  closeInfoWindow(): void {
    this.openInfoPin = null;
    if (this.infoWindow) {
      this.infoWindow.close();
    }
  }

  /** Pretty label for an order_type code, from the backend-provided list. */
  formatOrderType(code: string | null): string {
    if (!code) {
      return '';
    }
    return this.jobTypeLabelByValue.get(code) || code;
  }

  private fetchJobs(): void {
    this.loading = true;
    this.jobMapService
      .fetchActiveJobs()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pins) => {
          this.allPins = pins;
          this.loading = false;
          this.recomputeVisiblePins();
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private fetchCities(): void {
    this.jobMapService
      .fetchCities()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (cities) => {
          this.cityOptions = cities;
          this.cityById = new Map(cities.map((c) => [c.id, c]));
          this.applyCitySearch(this.citySearchControl.value || '');
        },
      });
  }

  /**
   * Turn a region title into the lowercase substring we look for inside an
   * address. "Vilniaus apskritis" → "vilni"; "Rīgas …" → "riga". Used by
   * the city filter to decide whether a pin belongs to a selected region.
   */
  private toCityMatchToken(title: string): string {
    const firstWord = (title || '').split(/\s+/)[0] || '';
    const normalized = normalizeForMatch(firstWord);
    return normalized.length > 3 ? normalized.slice(0, -2) : normalized;
  }

  private applyCitySearch(query: string): void {
    const q = normalizeForMatch((query || '').trim());
    if (!q) {
      this.filteredCityOptions = this.cityOptions.slice();
      return;
    }
    this.filteredCityOptions = this.cityOptions.filter((c) =>
      normalizeForMatch(c.title).includes(q),
    );
  }

  private recomputeVisiblePins(): void {
    if (!this.mapsReady) {
      return;
    }
    const centerLatLng = new google.maps.LatLng(this.center.lat, this.center.lng);

    const selectedCityIds = this.cityControl.value || [];
    // Derive the address-match token from each region's title — the same
    // heuristic that used to live on the backend: strip diacritics, take
    // the first word, drop the last two chars to absorb Baltic noun-case
    // variants (Vilniaus / Vilnius, Rigas / Riga).
    const cityMatchTokens = selectedCityIds
      .map((id) => this.toCityMatchToken(this.cityById.get(id)?.title || ''))
      .filter((m) => !!m);

    const selectedTypes = this.jobTypeControl.value || [];

    const filtered = this.allPins.filter((pin) => {
      if (selectedTypes.length && (!pin.orderType || !selectedTypes.includes(pin.orderType))) {
        return false;
      }
      if (cityMatchTokens.length) {
        // Backend already stripped diacritics from addressMatch; normalize
        // the address the same way so "Rīga" / "Riga" / "Rīgas" all match.
        const addrNormalized = normalizeForMatch(pin.address);
        const anyMatch = cityMatchTokens.some((token) => addrNormalized.includes(token));
        if (!anyMatch) {
          return false;
        }
      }
      return true;
    });

    const pinsWithDistance: JobMapPinWithDistance[] = filtered.map((pin) => {
      const pinLatLng = new google.maps.LatLng(pin.latitude, pin.longitude);
      const distanceMeters = google.maps.geometry.spherical.computeDistanceBetween(
        centerLatLng,
        pinLatLng,
      );
      return { ...pin, distanceKm: distanceMeters / 1000 };
    });

    if (this.hasResolvedLocation) {
      this.visiblePins = pinsWithDistance
        .filter((p) => p.distanceKm !== null && p.distanceKm <= this.radiusKm)
        .sort((a, b) => (a.distanceKm ?? 0) - (b.distanceKm ?? 0));
    } else {
      this.visiblePins = pinsWithDistance.map((p) => ({ ...p, distanceKm: null }));
    }

    this.redrawMarkers();
  }

  private redrawMarkers(): void {
    if (!this.googleMap?.googleMap) {
      setTimeout(() => this.redrawMarkers(), 0);
      return;
    }
    const map = this.googleMap.googleMap;

    this.clearMarkers();
    this.ensureInfoWindow();
    this.ensureMapClickCloses(map);

    const markers = this.visiblePins.map((pin) => {
      const color = PIN_COLOR_BY_STATUS[pin.status || ''] || PIN_COLOR_FALLBACK;
      const marker = new google.maps.Marker({
        position: { lat: pin.latitude, lng: pin.longitude },
        title: pin.positionTitle || '',
        icon: {
          path: PIN_SVG_PATH,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 1.6,
          scale: 1.6,
          anchor: new google.maps.Point(12, 22),
        },
      });
      // Stash the status on the marker so the cluster renderer can colour
      // the bubble by the majority status of its members.
      marker.set(MARKER_STATUS_KEY, pin.status || '');
      marker.addListener('click', () => this.openInfoWindowFor(pin));
      this.markerByPinId.set(pin.id, marker);
      return marker;
    });

    this.markerCluster = new MarkerClusterer({
      map,
      markers,
      renderer: this.clusterRenderer,
    });

    if (this.hasResolvedLocation) {
      this.candidateMarker = new google.maps.Marker({
        map,
        position: this.center,
        zIndex: 9999,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#1976d2',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });
    }
  }

  private ensureInfoWindow(): void {
    if (!this.infoWindow) {
      this.infoWindow = new google.maps.InfoWindow();
      this.infoWindow.addListener('closeclick', () => {
        this.openInfoPin = null;
      });
    }
  }

  /**
   * Since we hide the InfoWindow's built-in close button, give users a way
   * to dismiss the popup: click anywhere on the map (not on a pin) to close.
   */
  private ensureMapClickCloses(map: google.maps.Map): void {
    if (this.mapClickListenerAttached) {
      return;
    }
    this.mapClickListenerAttached = true;
    map.addListener('click', () => this.closeInfoWindow());
  }

  private openInfoWindowFor(pin: JobMapPin): void {
    if (!this.googleMap?.googleMap) {
      return;
    }
    this.ensureInfoWindow();
    const iw = this.infoWindow;
    if (!iw) {
      return;
    }

    // Re-compute distance for the info window so it stays accurate after
    // a card click that changes the center.
    const centerLatLng = new google.maps.LatLng(this.center.lat, this.center.lng);
    const pinLatLng = new google.maps.LatLng(pin.latitude, pin.longitude);
    const distanceKm = this.hasResolvedLocation
      ? google.maps.geometry.spherical.computeDistanceBetween(centerLatLng, pinLatLng) / 1000
      : null;

    const enriched: JobMapPinWithDistance = { ...pin, distanceKm };
    this.openInfoPin = enriched;
    iw.setContent(this.buildInfoWindowHtml(enriched));

    const marker = this.markerByPinId.get(pin.id);
    if (marker) {
      iw.open({ anchor: marker, map: this.googleMap.googleMap });
    } else {
      iw.setPosition({ lat: pin.latitude, lng: pin.longitude });
      iw.open({ map: this.googleMap.googleMap });
    }
  }

  /**
   * Build the InfoWindow body as raw HTML.
   *
   * Layout (top → bottom):
   *   <title>
   *   <company>
   *   [status chip] [type chip]    <- skipped if neither
   *   📍 address                    <- each row only if data present
   *   ↔  distance
   *   👤 recruiter
   *
   * Every section is conditional: nothing renders for missing values, so
   * the popup grows only as tall as the data demands. All user-controlled
   * text is HTML-escaped to prevent injection through addresses or names.
   */
  private buildInfoWindowHtml(pin: JobMapPinWithDistance): string {
    const esc = (s: string | null | undefined) => this.escapeHtml(s || '');

    const sections: string[] = [];

    // Header — title + company. Skip company line if absent.
    const headerLines: string[] = [];
    if (pin.positionTitle) {
      headerLines.push(`<div class="job-map-info__title">${esc(pin.positionTitle)}</div>`);
    }
    if (pin.companyName) {
      headerLines.push(`<div class="job-map-info__company">${esc(pin.companyName)}</div>`);
    }
    if (headerLines.length) {
      sections.push(`<div class="job-map-info__header">${headerLines.join('')}</div>`);
    }

    // Chips row — clickable order-id chip + coloured status pill + neutral
    // type pill. Each chip is independently conditional.
    const chips: string[] = [];
    if (pin.id) {
      // Reuses the FV table's existing `?orderId=` handling — see
      // free-vacancies-table.component.ts ngOnInit, which reads the param and
      // pre-fills the filter with "id:<orderId>". The URL carries the relay
      // global id (e.g. T3JkZXJUeXBlOjE1NjUw); the chip shows the readable
      // raw integer (#1234) + an open-in-new icon so recruiters can spot
      // the link at a glance.
      const href = this.fvUrlForOrder(pin.id);
      const label = !Number.isNaN(pin.orderId) ? `#${pin.orderId}` : '#order';
      chips.push(
        `<a class="job-map-info__chip job-map-info__chip--link" ` +
          `href="${esc(href)}" target="_blank" rel="noopener">` +
          esc(label) +
          `<i class="material-icons">open_in_new</i>` +
          `</a>`,
      );
    }
    if (pin.status) {
      chips.push(
        `<span class="job-map-info__chip job-map-info__chip--status job-map-info__chip--${esc(pin.status)}">${esc(pin.status)}</span>`,
      );
    }
    if (pin.orderType) {
      chips.push(
        `<span class="job-map-info__chip job-map-info__chip--type">${esc(this.formatOrderType(pin.orderType))}</span>`,
      );
    }
    if (chips.length) {
      sections.push(`<div class="job-map-info__chips">${chips.join('')}</div>`);
    }

    // Icon-led rows. Each only renders if its value is present.
    const rows: string[] = [];
    const row = (icon: string, content: string) =>
      `<div class="job-map-info__row"><i class="material-icons">${icon}</i><span>${content}</span></div>`;

    if (pin.address) {
      rows.push(row('place', esc(pin.address)));
    }
    if (pin.distanceKm !== null && pin.distanceKm !== undefined) {
      rows.push(row('straighten', `${pin.distanceKm.toFixed(1)} km`));
    }
    if (pin.recruiterName) {
      rows.push(row('person', esc(pin.recruiterName)));
    }
    if (rows.length) {
      sections.push(`<div class="job-map-info__rows">${rows.join('')}</div>`);
    }

    return `<div class="job-map-info">${sections.join('')}</div>`;
  }

  /**
   * Build a Free-Vacancies-list URL pointing at the given order. The FV list
   * already reads `?orderId=` from the URL and pre-fills its search bar with
   * `id:<orderId>`, so the recruiter lands directly on the filtered row.
   *
   * Takes the relay-encoded global id (e.g. "T3JkZXJUeXBlOjE1NjUw") — that's
   * what the FV table's existing search infrastructure expects to receive.
   *
   * IMPORTANT: do NOT strip the `=` padding from the base64 id. The backend's
   * Relay GlobalID decoder rejects unpadded ids with `"Invalid ID specified"`,
   * so the padding is load-bearing in the URL even though `atob()` would
   * tolerate its absence on the client side.
   *
   * Uses the deployed <base href> so the same call works in dev ("/")
   * and prod ("/app/") without an env-specific config.
   *
   * Public — called from the side-panel template as well as the popup builder.
   */
  fvUrlForOrder(relayId: string): string {
    const base = document.querySelector('base')?.getAttribute('href') || '/';
    return `${base.replace(/\/$/, '')}/free-vacancies/list?orderId=${encodeURIComponent(relayId)}`;
  }

  private escapeHtml(input: string): string {
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private clearMarkers(): void {
    this.markerByPinId.forEach((m) => m.setMap(null));
    this.markerByPinId.clear();
    if (this.markerCluster) {
      this.markerCluster.clearMarkers();
      this.markerCluster = undefined;
    }
    if (this.candidateMarker) {
      this.candidateMarker.setMap(null);
      this.candidateMarker = undefined;
    }
  }
}
